import { z } from "zod/v3";

export const DEFAULT_FONT_FAMILY = "Arial, Helvetica, sans-serif";

// Accept font stacks, including quoted and international family names, but no
// CSS declarations, escapes, or markup. These values also travel into SVGs.
const family = String.raw`(?:"[^"\\\x00-\x1f<>;{}]+"|'[^'\\\x00-\x1f<>;{}]+'|[\p{L}\p{N}_-][\p{L}\p{N}\p{M}_ .&-]*)`;
const fontFamilyPattern = new RegExp(`^${family}(?:\\s*,\\s*${family})*$`, "u");
export const fontFamilySchema = z.string().trim().min(1).max(300)
  // Unicode property escapes need JS's `u` flag, which JSON Schema cannot
  // transmit. Keep this validation server-side: exporting it as `pattern`
  // makes OpenAI reject the entire tool list before the agent can do any work.
  .refine(value => fontFamilyPattern.test(value), "Use a font family or comma-separated font stack")
  .default(DEFAULT_FONT_FAMILY)
  .describe('Font family or CSS font stack for text and buttons, e.g. "Inter, Arial, sans-serif". Use fontUrl to load a web font.');

export function fontSource(value: string): { kind: "stylesheet" | "file"; url: string } | null {
  if (!value || /[\s\\]/.test(value)) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    if (url.hostname === "fonts.googleapis.com" && !url.port &&
        /^\/css2?$/.test(url.pathname) && url.searchParams.get("family"))
      return { kind: "stylesheet", url: url.href };
    if (/\.(woff2?|ttf|otf)$/i.test(url.pathname))
      return { kind: "file", url: url.href };
  } catch { /* Invalid URLs are rejected by the schema and ignored by readers. */ }
  return null;
}

export const FONT_URL_DESCRIPTION = 'Optional web font URL: a Google Fonts HTTPS stylesheet (https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap), or an HTTPS .woff2, .woff, .ttf, or .otf file with CORS enabled. Match fontFamily to the stylesheet family; direct files use the first family and fontWeight. Empty string clears the source.';
export const fontUrlSchema = z.string().max(2000)
  .refine(value => value === "" || fontSource(value) !== null, "Use a Google Fonts HTTPS stylesheet or an HTTPS .woff2, .woff, .ttf, or .otf URL")
  .default("")
  .describe(FONT_URL_DESCRIPTION);

export function primaryFontFamily(stack: string) {
  const match = /^(?:"([^"]+)"|'([^']+)'|([^,]+))/.exec(stack.trim());
  return (match?.[1] ?? match?.[2] ?? match?.[3] ?? "Arial").trim();
}

// A quoted CSS string suitable for both FontFace sources and SVG styles.
export function fontCssString(value: string) {
  return `"${value.replace(/["\\\x00-\x1f<>&]/g, character =>
    `\\${character.codePointAt(0)!.toString(16)} `)}"`;
}

export const AGENT_FONT_GUIDE = 'Typography: text and button elements support fontFamily (a CSS font stack) and fontUrl. Set fontFamily alone for installed/system fonts. To load Google Fonts, set fontFamily to e.g. "Inter, Arial, sans-serif" and fontUrl to "https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap"; include every fontWeight you use in the URL. Reuse the same URL across elements. You can also link an HTTPS .woff2, .woff, .ttf, or .otf file with CORS enabled; it is registered under the first family in fontFamily at the element’s fontWeight. Other stylesheet hosts are not supported. Use a fallback family; unavailable web fonts fall back automatically. Set fontUrl to "" to clear it. Font choices persist in canvas views and exports; SVGs link remote fonts and require a viewer that permits external fonts. These controls are available through agent tools; there is no font picker in the editor.';
